import { IMAGES } from "@/lib/site";
import { Section, Overline, FadeIn, PageHero } from "@/components/Primitives";
import { ShieldCheck, FlaskConical, Boxes, ClipboardList, BadgeCheck, Trees } from "lucide-react";

const pillars = [
  {
    icon: ShieldCheck,
    title: "Origin Inspection",
    body: "Cargo is inspected and sampled at origin by trusted third-party agencies before loading.",
  },
  {
    icon: FlaskConical,
    title: "Laboratory Testing",
    body: "Moisture, foreign matter, protein, broken%, splits% and contaminants verified against contract specs.",
  },
  {
    icon: Boxes,
    title: "Bagging & Packaging",
    body: "PP bags, jumbo bags, vacuum-sealed packs or bulk — packaging matched to destination and use.",
  },
  {
    icon: ClipboardList,
    title: "Documentation Discipline",
    body: "Full LC, GAFTA-style documentation, fumigation, phyto & origin certificates handled in-house.",
  },
  {
    icon: BadgeCheck,
    title: "Compliance",
    body: "Adherence to destination food safety standards across UAE, EU, GCC, Africa and Asia.",
  },
  {
    icon: Trees,
    title: "Sustainable Sourcing",
    body: "Preference for partners committed to ethical farming and traceable supply.",
  },
];

export default function Quality() {
  return (
    <main data-testid="quality-page">
      <PageHero
        overline="Quality & Compliance"
        title={
          <>
            Spec on paper,
            <br />
            <em className="not-italic text-[#2A4D3B]">Spec in the container.</em>
          </>
        }
        subtitle="Every contract is honoured at grain level. We inspect, test and document — at origin, in transit, and at destination — so that what's agreed is what arrives."
        image={IMAGES.warehouse}
      />

      <Section testid="quality-pillars" className="bg-[#FDFBF7]">
        <div className="max-w-3xl mb-14">
          <Overline>The six pillars</Overline>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06] text-[#1A1A1A]">
            A quality discipline that protects every shipment.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <FadeIn key={p.title} delay={i * 0.06}>
              <article className="h-full rounded-3xl bg-white border border-[#EAE7E0] p-8 hover:-translate-y-1 hover:shadow-xl transition-all">
                <p.icon className="h-7 w-7 text-[#2A4D3B]" />
                <h3 className="mt-6 font-serif-display text-xl text-[#1A1A1A]">{p.title}</h3>
                <p className="mt-3 text-sm text-[#4A4540] leading-relaxed">{p.body}</p>
              </article>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section testid="quality-process" className="bg-[#F6F4F0]">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <FadeIn className="lg:col-span-6">
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border-4 border-[#FDFBF7] shadow-2xl">
              <img src={IMAGES.shipping} alt="Cargo shipping" className="h-full w-full object-cover" />
            </div>
          </FadeIn>
          <div className="lg:col-span-6">
            <Overline>The Farmico way</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06] text-[#1A1A1A]">
              Nothing ships unless it ships clean.
            </h2>
            <div className="mt-8 space-y-6 text-[#4A4540] leading-relaxed">
              <p>
                Quality is not a tagline — it is the operating system of our desk. Each shipment is
                attached to a named trader who is accountable for spec compliance from purchase order to
                bill of lading.
              </p>
              <p>
                We work exclusively with origin partners who allow third-party inspection at point of
                loading. Pre-shipment samples are matched against the contract before vessel sailing.
                Any deviation is resolved before goods leave port.
              </p>
              <p>
                That discipline is the reason long-standing buyers stay with us — and why new buyers
                trust us with their first shipment.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section testid="quality-certifications" className="bg-[#1F3A2D] text-[#F6F4F0]">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline dark>Certifications & standards</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06]">
              Working to the standards your market demands.
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 text-sm">
            {[
              "GAFTA-style contract conventions",
              "SGS / Bureau Veritas / Intertek inspection",
              "Phytosanitary certification at origin",
              "Fumigation certificates",
              "Halal certification on request",
              "FSSAI / EU food safety alignment",
              "Non-GMO certification on request",
              "Ethical sourcing declarations",
            ].map((c) => (
              <div key={c} className="rounded-2xl bg-white/5 border border-white/10 px-5 py-4">
                {c}
              </div>
            ))}
          </div>
        </div>
      </Section>
    </main>
  );
}
